import { Payment, PreviewPdf } from "@/pages/payments/interface";
import { Button, convertDollarToCents } from "@thdinc/hpf-shared";
import moment from "moment";
import logo from "../../assets/images/navlogo.png";
import React, { FC } from "react";
import Alert from "../alert/Alert";
import useLoading from "@/hooks/useLoading";
import { POST } from "@/services/api";
import { useAppDispatch, useAppSelector } from "@/hooks/hook";
import { API_ROUTES } from "@/services/environment";
import { resetPaymentForm } from "@/redux/slices/paymentFormSlice";
interface PaymentPreview {
    setPreviewData: (params: PreviewPdf) => void;
    previewData: PreviewPdf;
    currData: Payment | undefined;
    setPaymentInvoiceId?: (id: string) => void;
    setIsOpen?: (isOpen: boolean) => void;
}

const PaymentPreview: FC<PaymentPreview> = ({ setPreviewData, previewData, currData, setPaymentInvoiceId, setIsOpen }) => {
    const { isLoading, startLoading, stopLoading } = useLoading();
    const { refreshToken } = useAppSelector((store) => store.user);
    const dispatch = useAppDispatch();
    const { postPaymentChargeApi } = API_ROUTES;
    const { invoiceData } = previewData;
    const pointsToBeEarned = Number(invoiceData?.points_to_be_earned || 0);

    const onTableSubmit = async () => {
        try {
            const formData = new FormData();
            //table array values
            invoiceData?.spiritList.forEach((item: any, index: number) => {
                formData.append(`alcoholCharge[${index}][bottle_cost]`, convertDollarToCents(item.bottle_cost));
                formData.append(`alcoholCharge[${index}][number_of_bottles]`, item.number_of_bottles);
                formData.append(`alcoholCharge[${index}][bottle_total_amount]`, convertDollarToCents(item.bottle_total_amount));
                formData.append(`alcoholCharge[${index}][spirit_type]`, item.spirit_type);
                formData.append(`alcoholCharge[${index}][tax]`, convertDollarToCents(item.item_tax_rate));
                if (item.product_id == 'other') {
                    formData.append(`alcoholCharge[${index}][product_id]`, String(null));
                    formData.append(`alcoholCharge[${index}][name]`, item.brandName);
                } else {
                    formData.append(`alcoholCharge[${index}][product_id]`, item.product_id);
                    formData.append(`alcoholCharge[${index}][name]`, item.product_name);
                }
                // formData.append(`alcoholCharge[${index}][is_prefer]`, item.is_prefer);
            });
            //fixed values
            formData.append("business_id", String(currData?.business_id));
            formData.append("booking_id", String(currData?.id));
            formData.append("total_amount_charged", convertDollarToCents(invoiceData?.total_amount_charged));
            formData.append("hot_platform_fee", convertDollarToCents(String(invoiceData?.hot_platform_fee)));
            formData.append("tax_rate", convertDollarToCents(invoiceData?.taxRateTotal));
            formData.append("bartender_company_fee", convertDollarToCents(String(invoiceData?.bartender_company_fee)));
            formData.append("total_bonus", convertDollarToCents(invoiceData?.total_bonus));
            formData.append("HOWS", String(true));
            formData.append("store_name", String(invoiceData?.selectedStore?.name));
            formData.append("store_alcohol_location", String(invoiceData.store_location));
            formData.append("store_name_alcohol", String(invoiceData.store_name));
            startLoading();
            const res = await POST(`${postPaymentChargeApi}/${invoiceData?.storeId}`, formData, refreshToken);
            if (res.data) {
                if (res?.data?.success) {
                    setPreviewData({ isPreview: false, invoiceData: previewData?.invoiceData });
                    dispatch(resetPaymentForm());
                    setIsOpen?.(true)
                    setPaymentInvoiceId?.(res.data?.transcationId);
                    Alert({ message: res?.data?.message });
                } else {
                    Alert({ type: "error", message: res?.data?.message ?? "Something went wrong" });
                }
            }
        } catch (error) {
            Alert({ type: "error", message: "Something went wrong" });
        } finally {
            stopLoading();
        }


    }

    return (
        <div className="flex flex-col">
            <div className="w-full align-middle self-center font-sans ml-4 mr-4">
                {/* Header */}
                <header className="flex items-center mb-10">
                    <div className="flex items-center mr-10">
                        <img src={logo} className="w-[138px] h-9" />
                    </div>
                    <h1 className="text-base font-semibold text-black pt-4">Final Invoice Summary</h1>
                </header>
                {/* Details Section */}
                <section className="mb-10 flex justify-between items-start">
                    <div>
                        <h2 className="text-lg font-bold text-black mb-5">Details</h2>
                        <div className="space-y-2 text-sm">
                            <div className="flex items-center">
                                <span className="text-black">Booking #:</span>
                                <span className="text-gray-300 overflow-hidden whitespace-nowrap ml-1">
                                    ..................................
                                </span>
                                <span className="text-black ml-2">{currData?.id}</span>
                            </div>
                            <div className="flex items-center">
                                <span className="text-black">Store Name:</span>
                                <span className=" text-gray-300 overflow-hidden whitespace-nowrap ml-1">
                                    ...............................
                                </span>
                                <span className="text-black ml-2">{invoiceData?.selectedStore?.name}</span>
                            </div>
                            <div className="flex items-center">
                                <span className="text-black">Event Date:</span>
                                <span className="text-gray-300 overflow-hidden whitespace-nowrap ml-1">
                                    .................................
                                </span>
                                <span className="text-black ml-2">{moment(currData?.event_date).format("MMM DD, YYYY")}</span>
                            </div>
                            <div className="flex items-center">
                                <span className="text-black">Charge Date:</span>
                                <span className=" text-gray-300 overflow-hidden whitespace-nowrap ml-1">
                                    ..............................
                                </span>
                                <span className="text-black ml-2">{moment().format("MMM DD, YYYY")}</span>
                            </div>
                        </div>
                    </div>
                    <div></div>
                </section>
                {/* Alcohol Details Table */}
                <section className="mb-5">
                    <h2 className="text-lg font-bold text-black mb-1">Alcohol Details</h2>
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse text-sm bg-white border-t border-gray-300">
                            <thead>
                                <tr>
                                    <th className="bg-white p-3 text-left font-bold text-black border-b border-gray-300">
                                        Item Detail
                                    </th>
                                    <th className="bg-white p-3 text-left font-bold text-black border-b border-gray-300">
                                        Description
                                    </th>
                                    <th className="bg-white p-3 text-right font-bold text-black border-b border-gray-300">
                                        Quantity
                                    </th>
                                    <th className="bg-white p-3 text-right font-bold text-black border-b border-gray-300">
                                        Rate
                                    </th>
                                    <th className="bg-white p-3 text-right font-bold text-black border-b border-gray-300">
                                        Amount
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {previewData?.invoiceData?.spiritList &&
                                    previewData?.invoiceData?.spiritList?.map((item: any, index: any) => (
                                        <tr key={index}>
                                            <td className="p-2.5 border-b border-gray-100 text-black font-normal">
                                                {item?.spirit_type?.charAt(0)?.toUpperCase() + item?.spirit_type?.slice(1)}
                                            </td>
                                            <td className="p-2.5 border-b border-gray-100 text-black font-normal">
                                                {item?.product_name}
                                            </td>
                                            <td className="p-2.5 border-b border-gray-100 text-black font-normal text-right">
                                                {item?.number_of_bottles}
                                            </td>
                                            <td className="p-2.5 border-b border-gray-100 text-black font-normal text-right">
                                                ${item?.bottle_cost}.00
                                            </td>
                                            <td className="p-2.5 border-b border-gray-100 text-black font-normal text-right">
                                                ${item?.bottle_total_amount - item?.item_tax_rate}.00
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                </section>
                {/* Totals Section */}
                <section className="flex">
                    <div className="flex-1"></div>
                    <div className="flex-1 mr-2">
                        <div className="flex justify-between mb-2 text-sm">
                            <span className="text-black mr-10">Subtotal</span>
                            <span className="text-black min-w-[80px] text-right">
                                ${Number(previewData?.invoiceData?.sub_total_charged).toFixed(2)}
                            </span>
                        </div>
                        <div className="flex justify-between mb-2 text-sm">
                            <span className="text-black mr-10">Tax
                                {/* ({invoiceData.taxRate}%) */}
                            </span>
                            <span className="text-black min-w-[80px] text-right">
                                ${Number(previewData?.invoiceData?.taxRateTotal).toFixed(2)}
                            </span>
                        </div>
                        {Number(invoiceData?.bartender_company_fee) > 0 && <div className="flex justify-between mb-2 text-sm">
                            <span className="text-black mr-10">Bartending Company Fee{pointsToBeEarned > 0 ? ` (${pointsToBeEarned} pts)` : ""}
                                {/* ({invoiceData.taxRate}%) */}
                            </span>
                            {/* <span className="text-black min-w-[80px] text-right">
                                -${Number(invoiceData?.bartender_company_fee).toFixed(2)}
                            </span> */}
                        </div>}
                        <div className="flex justify-between mt-4 pt-4 border-t border-gray-300 text-lg font-bold">
                            <span className="text-black mr-10">Total</span>
                            <span className="text-black min-w-[80px] text-right">
                                ${Number(previewData?.invoiceData?.preview_amount_charged).toFixed(2)}
                            </span>
                        </div>
                    </div>
                </section>
            </div>
            <div className="flex justify-end mb-[74px]">
                <Button
                    type="button"
                    disabled={isLoading}
                    onClick={() => onTableSubmit()}
                    className={`text-center px-6 rounded-full py-2 ${"bg-primary-red hover:bg-primary-red-button-hover text-white mt-3 mr-1"}`}>
                    {isLoading ? "Processing..." : "Charge"}
                </Button>
            </div>
        </div>
    )
}
export default PaymentPreview;